/**
 * English copy — the source of truth for the site's text.
 *
 * Marketing copy is lifted from "Two Milks Landing.dc.html" and follows
 * "Marketing Kit/Captions/voice-and-copy-rules.txt". Rewriting it here would
 * put the site out of step with the Instagram captions and the app itself, so
 * changes should go back into the brand documents too.
 *
 * Two rules from those documents that are easy to break by accident: no
 * exclamation marks anywhere, and no emoji anywhere. The brand name is never
 * translated — it is "Two Milks" in both languages.
 *
 * The English text is also the legally binding version of the notices — see
 * docs/legal.html.
 */
const en = {
  meta: {
    title: 'Two Milks — one grocery list for everyone in the house',
    description:
      'One grocery list the whole house shares, crossed off the moment someone picks something up. Add it from the bus. Photograph the exact one you mean.',
  },

  nav: {
    how: 'How it works',
    features: 'Features',
    faq: 'Questions',
    pricing: 'Pricing',
    cta: 'Get the app',
    skipToContent: 'Skip to content',
    menu: 'Menu',
    closeMenu: 'Close menu',
    languageLabel: 'Language',
  },

  /*
   * The two store buttons, used by the hero and again by the closing panel.
   * Shared rather than duplicated so the two can never drift apart.
   *
   * Both open the waitlist today, because the app is not published yet.
   */
  store: {
    ios: 'Download for iPhone',
    android: 'Get it on Android',
  },

  hero: {
    // Two lines, deliberately. The joke is the line break.
    headlineA: 'You both bought milk.',
    headlineB: 'Nobody bought bread.',
    body: 'One list the whole house shares, crossed off the moment someone picks something up. Add it from the bus. Photograph the exact one you mean.',
    reassurance:
      'Free for a house of three. We don’t connect to any shop, and we never sell what you buy.',
  },

  mockup: {
    houseLabel: 'House',
    houseName: 'Flat 4B',
    houseMeta: '6 left · 1 bought',
    shop: 'Shop',
    add: 'Add an item',
    produce: 'Produce',
    dairy: 'Dairy & eggs',
    items: {
      tomatoes: 'Tomatoes on the vine',
      lemons: 'Lemons',
      oatMilk: 'Barista oat milk',
      coriander: 'Coriander',
      butter: 'Salted butter',
      eggs: 'Eggs, free range',
    },
    by: {
      tomatoes: 'Mari',
      lemons: 'Sam · photo',
      oatMilk: 'Mari · photo',
      coriander: 'Tom',
      butter: 'Tom',
      eggs: 'Mari',
    },
    tabs: {
      list: 'LIST',
      feed: 'FEED',
      house: 'HOUSE',
      settings: 'SETTINGS',
    },
  },

  problems: {
    items: [
      {
        title: 'Two milks',
        body: 'You both bought one. Items cross off the instant someone picks them up, so the second trip is never a repeat of the first.',
      },
      {
        title: 'The wrong one',
        body: '"Not the seeded loaf." Photograph the exact product and the note travels with the item into the aisle.',
      },
      {
        title: 'Who paid',
        body: 'Every trip logs a total and a split, so the awkward maths at the end of the month is already done.',
      },
    ],
  },

  how: {
    heading: 'Three taps to set up. One tap for everything after.',
    steps: [
      {
        number: '01',
        title: 'Start a house',
        body: 'Name the place, get a code like',
        code: '4B-PLUM',
      },
      {
        number: '02',
        title: 'Send the code',
        body: 'Partner, flatmates, your mum. They type it once and they’re in — no accounts to manage.',
      },
    ],
    feature: {
      number: '03',
      title: 'Add it the second you notice',
      body: 'Last coffee pod goes in the machine — add it standing there. That’s the whole habit, and it’s the only one that works.',
    },
  },

  features: {
    heading: 'Built for the way a house actually shops.',
    photos: {
      title: 'Photos for the fussy stuff',
      body: 'Shoot the label or the shelf tag. Whoever shops sees it full-screen in the aisle instead of guessing between eleven oat milks.',
      note: '"Only the barista one froths properly — the regular one splits in coffee."',
      shelfTag: 'Shelf tag',
      label: 'The label',
    },
    categories: {
      title: 'Your categories, your aisles',
      body: 'Rename them and drag them into the order of your actual shop. Everyone in the house gets the same order.',
      rows: [
        { label: 'Produce', count: '6' },
        { label: 'Bakery', count: '2' },
        { label: 'Dairy & eggs', count: '4' },
      ],
    },
    shopping: {
      overline: 'Shopping mode',
      title: 'A screen you can read at arm’s length.',
      body: 'Start a trip and the list goes big, dark and aisle-ordered. Large tap targets, nothing else on screen, and everyone at home watches things get crossed off as you walk.',
      stats: [
        { value: '18px', label: 'row text, one-handed' },
        { value: 'Live', label: 'sync as you walk' },
      ],
      progressLabel: 'of 14 left',
      shopper: 'Sam is shopping',
      items: ['Tomatoes on the vine', 'Barista oat milk', 'Spinach'],
    },
  },

  pricing: {
    title: 'Free. That\u2019s the plan.',
    body: 'Everything you need to stop buying two of things, for the whole house you share it with \u2014 and nothing the house writes down is ever deleted.',
    free: {
      name: 'The house',
      price: 'Free',
      per: 'Forever, for up to three people.',
      items: [
        'One shared list, crossed off live',
        'Photos and notes on any item',
        'Shopping mode, offline',
        'Trip totals and splits',
        '30 days of history',
      ],
      cta: 'Start a house',
    },
    kept: {
      name: 'The shape of a house',
      note: 'The limits, stated plainly — and what never changes.',
      items: [
        'Three people to a house',
        'Two houses to a person',
        'The last 30 days of trips on view',
        'Everything older stays on record',
        'Nothing is ever deleted',
      ],
    },
    whyTitle: 'Why there are limits.',
    whyBody:
      'Two Milks is deciding what it becomes, and the limits are how it listens. They are real — three people, 30 days on view — but nothing older is ever deleted, so if the limits ever lift, everything from before is still there.',
  },

  faq: {
    heading: 'The things people ask before they invite the house.',
    items: [
      {
        q: 'Does everyone need an account?',
        a: 'One person creates the house; everyone else joins with the code. Accounts are just an email and a password.',
      },
      {
        q: 'What if I lose signal in the shop?',
        a: 'Everything works offline and syncs the second you’re back — nothing you tick off in a supermarket basement is lost.',
      },
      {
        q: 'What does it cost?',
        a: 'It\u2019s free. A house holds three people, a person belongs to up to two houses, and the app shows the last 30 days of trips; everything older stays on record and is never deleted.',
      },
      {
        q: 'When is it out?',
        a: 'We’re finishing it now. Join the waitlist and you’ll get one email the day it reaches the App Store and Google Play — nothing else.',
      },
    ],
  },

  finalCta: {
    heading: 'Get the milk question out of your messages.',
    body: 'Set up a house in under a minute. Invite whoever lives with you.',
  },

  waitlist: {
    badge: 'Coming soon',
    title: 'Two Milks isn’t out yet.',
    body: 'Leave your email and we’ll send you one message the day it lands on the App Store and Google Play. One email. Nothing else, ever.',
    emailLabel: 'Your email address',
    emailPlaceholder: 'you@example.com',
    submit: 'Join the waitlist',
    submitting: 'Joining…',
    close: 'Close',
    privacyNote: 'We only use your address to tell you the app is out.',
    privacyLink: 'How we handle your data',
    successTitle: 'You’re on the list.',
    successBody:
      'Check your inbox — there’s a confirmation on its way. We’ll be in touch the day it launches.',
    successDone: 'Close',
    errors: {
      missing_email: 'Please enter your email address.',
      invalid_email: 'That doesn’t look like a valid email address.',
      email_too_long: 'That email address is too long.',
      unknown_product: 'Something went wrong on our side. Please try again.',
      rate_limited: 'Too many attempts. Please wait a few minutes and try again.',
      spam_detected: 'Something went wrong. Please try again.',
      send_failed:
        'We couldn’t save that just now. Please try again, or email hola@buildpassing.com.',
      server_error:
        'Something went wrong on our side. Please try again, or email hola@buildpassing.com.',
      network: 'We couldn’t reach the server. Check your connection and try again.',
    },
  },

  footer: {
    tagline: 'One grocery list for everyone in the house.',
    builtBy: 'Built by',
    columns: {
      product: 'Product',
      legal: 'Legal',
      company: 'Company',
    },
    links: {
      how: 'How it works',
      features: 'Features',
      faq: 'Questions',
      pricing: 'Pricing',
      waitlist: 'Join the waitlist',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      cookies: 'Cookie Policy',
      deleteAccount: 'Delete your account',
      support: 'Support',
      contact: 'Contact',
      instagram: 'Instagram',
      buildpassing: 'BuildPassing',
    },
    rights: 'All rights reserved.',
  },

  notFound: {
    title: 'That page isn’t here.',
    body: 'The link may be out of date, or we may have moved it. The front page is a good place to start.',
    cta: 'Back to the front page',
  },

  legal: {
    lastUpdated: 'Last updated',
    effectiveDate: 'Effective date',
    contents: 'Contents',
    questionsTitle: 'Questions about this document?',
    questionsBody: 'Write to us and a person will answer.',
    bindingNotice:
      'This document is also available in Spanish. If the two versions disagree, the English version governs.',
  },
};

/* Deliberately not `as const`. A literal type here would force the Spanish
   bundle to repeat the English strings exactly, which is the opposite of what
   we want — we need the same *shape*, not the same words. */
export default en;
